import { type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  apiRequest,
  ClientApiError,
  deleteRequest,
  postJson,
  type ApiProblem,
} from "../auth/api";
import { Checkbox } from "../components/ui/Checkbox";
import { Badge, Button, Input, Select, Skeleton, Textarea } from "../components/ui";
import { TermsAcceptance } from "./AuthPages";

interface PrivateAccount {
  email: string;
  phone: string | null;
  emailVerified: boolean;
  phoneVerified: boolean;
  displayName: string;
  publicSlug: string;
  bio: string | null;
  avatarUrl: string | null;
  visibility: string;
  legalName: string | null;
  birthDate: string | null;
  provinceCode: string | null;
  experienceYears: number | null;
  experienceSummary: string | null;
  specialties: string[];
  links: string[];
  privacy: {
    showProvince?: boolean;
    showExperience?: boolean;
    showSpecialties?: boolean;
  };
  onboardingComplete: boolean;
}

interface Province {
  code: string;
  name: string;
  type: string;
}

interface SessionItem {
  id: string;
  current: boolean;
  createdAt: string;
  lastSeenAt: string | null;
  expiresAt: string;
  device: string;
  userAgent: string | null;
}

interface MfaState {
  enabled: boolean;
  pending: boolean;
}

function ProblemBox({ problem }: { problem: ApiProblem | null }) {
  if (!problem) return null;
  return (
    <div className="form-alert form-alert--error" role="alert">
      <strong>{problem.message}</strong>
      {problem.fieldErrors ? (
        <ul>
          {Object.values(problem.fieldErrors).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
      <small>Mã yêu cầu: {problem.requestId}</small>
    </div>
  );
}

function useProblemState() {
  const [problem, setProblem] = useState<ApiProblem | null>(null);
  const capture = useCallback((error: unknown) => {
    setProblem(
      error instanceof ClientApiError
        ? error.problem
        : {
            code: "REQUEST_FAILED",
            message: "Không thể xử lý yêu cầu.",
            requestId: "unknown",
          },
    );
  }, []);
  return { problem, setProblem, capture };
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const { principal, refresh } = useAuth();
  const { problem, setProblem, capture } = useProblemState();
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [legalName, setLegalName] = useState("");
  const [displayName, setDisplayName] = useState(principal?.displayName ?? "");
  const [birthDate, setBirthDate] = useState("");
  const [phone, setPhone] = useState("");
  const [provinceCode, setProvinceCode] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void apiRequest<{ provinces: Province[] }>("/api/v1/auth/provinces")
      .then((response) => setProvinces(response.provinces))
      .catch(capture);
  }, [capture]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setProblem(null);
    setSubmitting(true);
    try {
      await postJson("/api/v1/auth/onboarding", {
        legalName,
        displayName,
        birthDate,
        phone,
        provinceCode,
        acceptTerms: accepted,
      });
      await refresh();
      navigate("/member", { replace: true });
    } catch (error) {
      capture(error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="member-page member-page--narrow">
      <header className="page-heading">
        <p className="eyebrow">Onboarding bắt buộc</p>
        <h1>Hoàn tất tài khoản</h1>
        <p>
          Họ tên, ngày sinh và số điện thoại chỉ dùng cho xác minh và vận hành. Hồ sơ công
          khai không trả các trường này.
        </p>
      </header>
      <ProblemBox problem={problem} />
      <form className="settings-form panel" onSubmit={submit} noValidate>
        <div className="form-grid">
          <Input
            label="Họ và tên"
            autoComplete="name"
            value={legalName}
            onChange={(event) => setLegalName(event.target.value)}
            error={problem?.fieldErrors?.legalName}
            required
          />
          <Input
            label="Tên hiển thị công khai"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            error={problem?.fieldErrors?.displayName}
            required
          />
          <Input
            label="Ngày sinh"
            type="date"
            autoComplete="bday"
            value={birthDate}
            onChange={(event) => setBirthDate(event.target.value)}
            error={problem?.fieldErrors?.birthDate}
            required
          />
          <Input
            label="Số điện thoại Việt Nam"
            type="tel"
            autoComplete="tel"
            inputMode="tel"
            placeholder="0901234567"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            hint="Hệ thống tự chuẩn hóa về +84. Số điện thoại phải duy nhất."
            error={problem?.fieldErrors?.phone}
            required
          />
          <Select
            label="Tỉnh/thành"
            value={provinceCode}
            onChange={(event) => setProvinceCode(event.target.value)}
            error={problem?.fieldErrors?.provinceCode}
            required
          >
            <option value="">Chọn tỉnh/thành</option>
            {provinces.map((province) => (
              <option key={province.code} value={province.code}>
                {province.name}
              </option>
            ))}
          </Select>
        </div>
        <TermsAcceptance
          accepted={accepted}
          setAccepted={setAccepted}
          error={problem?.fieldErrors?.acceptTerms}
        />
        <Button type="submit" disabled={submitting}>
          {submitting ? "Đang hoàn tất…" : "Hoàn tất tài khoản"}
        </Button>
      </form>
    </section>
  );
}

export function MemberDashboardPage() {
  const { principal } = useAuth();
  return (
    <section className="member-page">
      <header className="page-heading">
        <p className="eyebrow">Không gian thành viên</p>
        <h1>Xin chào, {principal?.displayName ?? "thành viên"}</h1>
        <p>
          Tài khoản đã xác minh và onboarding hoàn tất. Các module hồ sơ chó được triển khai
          ở Prompt 04.
        </p>
      </header>
      <div className="dashboard-grid">
        <article className="panel">
          <Badge tone="success">Tài khoản hoạt động</Badge>
          <h2>Hồ sơ công khai</h2>
          <p>
            Public slug: <code>{principal?.publicSlug}</code>
          </p>
          {principal ? (
            <Link className="text-link" to={`/u/${principal.publicSlug}`}>
              Xem hồ sơ công khai
            </Link>
          ) : null}
        </article>
        <article className="panel">
          <h2>Bảo mật tài khoản</h2>
          <p>Kiểm tra phiên đăng nhập, mật khẩu, Google identity và xác thực 2 bước.</p>
          <Link className="text-link" to="/member/security">
            Mở cài đặt bảo mật
          </Link>
        </article>
      </div>
    </section>
  );
}

export function ProfileSettingsPage() {
  const { refresh } = useAuth();
  const { problem, setProblem, capture } = useProblemState();
  const [account, setAccount] = useState<PrivateAccount | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [experienceYears, setExperienceYears] = useState("");
  const [experienceSummary, setExperienceSummary] = useState("");
  const [specialties, setSpecialties] = useState("");
  const [links, setLinks] = useState("");
  const [showProvince, setShowProvince] = useState(true);
  const [showExperience, setShowExperience] = useState(true);
  const [showSpecialties, setShowSpecialties] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  const applyAccount = useCallback((value: PrivateAccount) => {
    setAccount(value);
    setDisplayName(value.displayName);
    setBio(value.bio ?? "");
    setExperienceYears(value.experienceYears?.toString() ?? "");
    setExperienceSummary(value.experienceSummary ?? "");
    setSpecialties(value.specialties.join(", "));
    setLinks(value.links.join("\n"));
    setShowProvince(value.privacy.showProvince !== false);
    setShowExperience(value.privacy.showExperience !== false);
    setShowSpecialties(value.privacy.showSpecialties !== false);
  }, []);

  const loadAccount = useCallback(async () => {
    const response = await apiRequest<{ account: PrivateAccount }>("/api/v1/auth/account");
    applyAccount(response.account);
  }, [applyAccount]);

  useEffect(() => {
    void loadAccount().catch(capture);
  }, [capture, loadAccount]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setProblem(null);
    setMessage(null);
    setSaving(true);
    try {
      const response = await postJson<{ message: string }>("/api/v1/auth/account", {
        displayName,
        bio,
        experienceYears: experienceYears === "" ? null : Number(experienceYears),
        experienceSummary,
        specialties: specialties
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
        links: links
          .split("\n")
          .map((item) => item.trim())
          .filter(Boolean),
        privacy: { showProvince, showExperience, showSpecialties },
      });
      setMessage(response.message);
      await refresh();
      await loadAccount();
    } catch (error) {
      capture(error);
    } finally {
      setSaving(false);
    }
  }

  async function uploadAvatar(file: File | undefined) {
    if (!file) return;
    setProblem(null);
    setMessage(null);
    setAvatarUploading(true);
    try {
      const form = new FormData();
      form.set("avatar", file);
      await apiRequest<{ avatarUrl: string }>("/api/v1/auth/avatar", {
        method: "POST",
        body: form,
      });
      await loadAccount();
      setMessage("Đã cập nhật ảnh đại diện.");
    } catch (error) {
      capture(error);
    } finally {
      setAvatarUploading(false);
    }
  }

  if (!account && !problem) return <Skeleton lines={8} />;

  return (
    <section className="member-page">
      <header className="page-heading">
        <p className="eyebrow">Hồ sơ tài khoản</p>
        <h1>Thông tin hiển thị</h1>
        <p>Email, số điện thoại và ngày sinh chỉ xuất hiện trong trang riêng tư này.</p>
      </header>
      <ProblemBox problem={problem} />
      {message ? <div className="form-alert form-alert--success">{message}</div> : null}
      <div className="settings-layout">
        <aside className="panel account-private-card">
          <div className="profile-avatar">
            {account?.avatarUrl ? (
              <img src={account.avatarUrl} alt="Ảnh đại diện" />
            ) : (
              <span aria-hidden="true">{account?.displayName.slice(0, 1).toUpperCase()}</span>
            )}
          </div>
          <label className="file-button">
            <span>{avatarUploading ? "Đang tải…" : "Chọn ảnh đại diện"}</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              disabled={avatarUploading}
              onChange={(event) => void uploadAvatar(event.target.files?.[0])}
            />
          </label>
          <dl>
            <div>
              <dt>Email</dt>
              <dd>{account?.email}</dd>
            </div>
            <div>
              <dt>Điện thoại</dt>
              <dd>{account?.phone ?? "Chưa có"}</dd>
            </div>
            <div>
              <dt>Ngày sinh</dt>
              <dd>{account?.birthDate ?? "Chưa có"}</dd>
            </div>
            <div>
              <dt>Public slug</dt>
              <dd>
                <code>{account?.publicSlug}</code>
              </dd>
            </div>
          </dl>
        </aside>
        <form className="settings-form panel" onSubmit={save}>
          <Input
            label="Tên hiển thị"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            error={problem?.fieldErrors?.displayName}
          />
          <Textarea
            label="Giới thiệu"
            value={bio}
            onChange={(event) => setBio(event.target.value)}
            hint="Tối đa 1.200 ký tự."
          />
          <Input
            label="Số năm kinh nghiệm"
            type="number"
            min={0}
            max={80}
            value={experienceYears}
            onChange={(event) => setExperienceYears(event.target.value)}
            error={problem?.fieldErrors?.experienceYears}
          />
          <Textarea
            label="Kinh nghiệm working dog"
            value={experienceSummary}
            onChange={(event) => setExperienceSummary(event.target.value)}
          />
          <Input
            label="Lĩnh vực"
            value={specialties}
            onChange={(event) => setSpecialties(event.target.value)}
            hint="Phân cách bằng dấu phẩy, ví dụ IGP, obedience, detection."
          />
          <Textarea
            label="Liên kết công khai"
            value={links}
            onChange={(event) => setLinks(event.target.value)}
            hint="Mỗi dòng một URL HTTPS."
          />
          <fieldset className="privacy-fieldset">
            <legend>Quyền riêng tư hồ sơ công khai</legend>
            <Checkbox
              checked={showProvince}
              onChange={setShowProvince}
              label="Hiển thị tỉnh/thành"
            />
            <Checkbox
              checked={showExperience}
              onChange={setShowExperience}
              label="Hiển thị kinh nghiệm"
            />
            <Checkbox
              checked={showSpecialties}
              onChange={setShowSpecialties}
              label="Hiển thị lĩnh vực"
            />
          </fieldset>
          <Button type="submit" disabled={saving}>
            {saving ? "Đang lưu…" : "Lưu hồ sơ"}
          </Button>
        </form>
      </div>
    </section>
  );
}

export function SecuritySettingsPage() {
  const navigate = useNavigate();
  const { clear } = useAuth();
  const { problem, setProblem, capture } = useProblemState();
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [mfa, setMfa] = useState<MfaState | null>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [setup, setSetup] = useState<{
    methodId: string;
    secret: string;
    uri: string;
  } | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [sessionResponse, mfaResponse] = await Promise.all([
      apiRequest<{ sessions: SessionItem[] }>("/api/v1/auth/sessions"),
      apiRequest<MfaState>("/api/v1/auth/mfa"),
    ]);
    setSessions(sessionResponse.sessions);
    setMfa(mfaResponse);
  }, []);

  useEffect(() => {
    void load().catch(capture);
  }, [capture, load]);

  async function changePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setProblem(null);
    setMessage(null);
    if (newPassword !== confirmPassword) {
      setProblem({
        code: "VALIDATION_ERROR",
        message: "Dữ liệu chưa hợp lệ.",
        requestId: "client",
        fieldErrors: { confirmPassword: "Mật khẩu nhập lại không khớp." },
      });
      return;
    }
    try {
      const response = await postJson<{ message: string }>("/api/v1/auth/password", {
        currentPassword,
        newPassword,
      });
      setMessage(response.message);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      await load();
    } catch (error) {
      capture(error);
    }
  }

  async function revoke(id: string, current: boolean) {
    setProblem(null);
    try {
      await deleteRequest(`/api/v1/auth/sessions/${encodeURIComponent(id)}`);
      if (current) {
        clear();
        navigate("/login", { replace: true });
      } else {
        await load();
      }
    } catch (error) {
      capture(error);
    }
  }

  async function logoutAll() {
    setProblem(null);
    try {
      await postJson("/api/v1/auth/sessions/logout-all", {});
      clear();
      navigate("/login", { replace: true });
    } catch (error) {
      capture(error);
    }
  }

  async function startMfa() {
    setProblem(null);
    try {
      setSetup(
        await postJson<{ methodId: string; secret: string; uri: string }>(
          "/api/v1/auth/mfa/setup",
          {},
        ),
      );
    } catch (error) {
      capture(error);
    }
  }

  async function confirmMfa() {
    if (!setup) return;
    setProblem(null);
    try {
      const response = await postJson<{ recoveryCodes: string[] }>(
        "/api/v1/auth/mfa/confirm",
        { methodId: setup.methodId, code: totpCode },
      );
      setRecoveryCodes(response.recoveryCodes);
      setSetup(null);
      setTotpCode("");
      await load();
    } catch (error) {
      capture(error);
    }
  }

  return (
    <section className="member-page">
      <header className="page-heading">
        <p className="eyebrow">Bảo mật</p>
        <h1>Phiên đăng nhập và xác thực</h1>
        <p>Thu hồi session có hiệu lực ngay tại database; cookie cũ không thể tiếp tục truy cập.</p>
      </header>
      <ProblemBox problem={problem} />
      {message ? <div className="form-alert form-alert--success">{message}</div> : null}

      <div className="security-grid">
        <article className="panel">
          <h2>Thiết bị đang đăng nhập</h2>
          {sessions.length === 0 ? (
            <Skeleton lines={3} />
          ) : (
            <div className="session-list">
              {sessions.map((session) => (
                <div className="session-item" key={session.id}>
                  <div>
                    <strong>{session.device}</strong>
                    {session.current ? <Badge tone="success">Phiên hiện tại</Badge> : null}
                    <small>Lần cuối: {session.lastSeenAt ?? session.createdAt}</small>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void revoke(session.id, session.current)}
                  >
                    Thu hồi
                  </Button>
                </div>
              ))}
            </div>
          )}
          <Button variant="danger" onClick={() => void logoutAll()}>
            Đăng xuất tất cả thiết bị
          </Button>
        </article>

        <article className="panel">
          <h2>Google identity</h2>
          <p>Chỉ cho phép liên kết Google dùng đúng email đã xác minh của tài khoản hiện tại.</p>
          <a
            className="oauth-button"
            href="/api/v1/auth/google/start?intent=link&returnTo=%2Fmember%2Fsecurity"
          >
            Liên kết tài khoản Google
          </a>
        </article>

        <article className="panel">
          <h2>Xác thực 2 bước</h2>
          {mfa?.enabled ? (
            <>
              <Badge tone="success">Đang bật</Badge>
              <p>TOTP đang bảo vệ các lần đăng nhập bằng mật khẩu.</p>
            </>
          ) : (
            <>
              <Badge tone="warning">Chưa bật</Badge>
              <p>
                Thành viên có thể bật tùy chọn; admin sẽ bị bắt buộc ở phần security tiếp theo.
              </p>
            </>
          )}
          {!mfa?.enabled && !setup ? (
            <Button onClick={() => void startMfa()}>Thiết lập 2FA</Button>
          ) : null}
          {setup ? (
            <div className="mfa-setup">
              <p>Thêm secret này vào ứng dụng xác thực:</p>
              <code>{setup.secret}</code>
              <details>
                <summary>URI cấu hình</summary>
                <code>{setup.uri}</code>
              </details>
              <Input
                label="Mã 6 chữ số"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={totpCode}
                onChange={(event) =>
                  setTotpCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                }
              />
              <Button onClick={() => void confirmMfa()}>Xác nhận 2FA</Button>
            </div>
          ) : null}
          {recoveryCodes.length > 0 ? (
            <div className="recovery-codes" role="status">
              <strong>Lưu các mã khôi phục ngay bây giờ</strong>
              <p>Hệ thống chỉ hiển thị một lần.</p>
              <code>{recoveryCodes.join("\n")}</code>
            </div>
          ) : null}
        </article>

        <form className="panel settings-form" onSubmit={changePassword}>
          <h2>Đổi mật khẩu</h2>
          <Input
            label="Mật khẩu hiện tại"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            error={problem?.fieldErrors?.currentPassword}
          />
          <Input
            label="Mật khẩu mới"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            error={problem?.fieldErrors?.newPassword}
          />
          <Input
            label="Nhập lại mật khẩu mới"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            error={problem?.fieldErrors?.confirmPassword}
          />
          <Button type="submit">Đổi mật khẩu</Button>
        </form>
      </div>
    </section>
  );
}

interface PublicProfile {
  displayName: string;
  publicSlug: string;
  bio: string | null;
  avatarUrl: string | null;
  province: string | null;
  experienceYears: number | null;
  experienceSummary: string | null;
  specialties: string[];
  links: string[];
}

export function PublicMemberProfilePage() {
  const { slug = "" } = useParams();
  const { problem, capture } = useProblemState();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const safeSlug = useMemo(() => encodeURIComponent(slug), [slug]);

  useEffect(() => {
    void apiRequest<{ profile: PublicProfile }>(`/api/v1/profiles/${safeSlug}`)
      .then((response) => setProfile(response.profile))
      .catch(capture);
  }, [capture, safeSlug]);

  if (problem) return <ProblemBox problem={problem} />;
  if (!profile) return <Skeleton lines={7} />;

  return (
    <section className="public-profile">
      <div className="public-profile__header">
        <div className="profile-avatar profile-avatar--large">
          {profile.avatarUrl ? (
            <img src={profile.avatarUrl} alt={`Ảnh của ${profile.displayName}`} />
          ) : (
            <span aria-hidden="true">{profile.displayName.slice(0, 1).toUpperCase()}</span>
          )}
        </div>
        <div>
          <p className="eyebrow">Hồ sơ thành viên</p>
          <h1>{profile.displayName}</h1>
          <p>
            <code>/{profile.publicSlug}</code>
          </p>
        </div>
      </div>
      <div className="dashboard-grid">
        <article className="panel">
          <h2>Giới thiệu</h2>
          <p>{profile.bio || "Thành viên chưa thêm giới thiệu."}</p>
          {profile.province ? (
            <p>
              <strong>Khu vực:</strong> {profile.province}
            </p>
          ) : null}
        </article>
        <article className="panel">
          <h2>Kinh nghiệm</h2>
          {profile.experienceYears !== null ? (
            <p>{profile.experienceYears} năm kinh nghiệm</p>
          ) : null}
          <p>{profile.experienceSummary || "Chưa công khai thông tin kinh nghiệm."}</p>
          {profile.specialties.length > 0 ? (
            <div className="tag-list">
              {profile.specialties.map((item) => (
                <Badge key={item}>{item}</Badge>
              ))}
            </div>
          ) : null}
        </article>
      </div>
      {profile.links.length > 0 ? (
        <div className="panel public-links">
          <h2>Liên kết</h2>
          {profile.links.map((link) => (
            <a key={link} href={link} target="_blank" rel="noreferrer">
              {link}
            </a>
          ))}
        </div>
      ) : null}
    </section>
  );
}
